<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConversationSidebars } from 'dashboard/composables/useConversationSidebars';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import { clampSidebarWidthPercent } from 'dashboard/helper/conversationSidebarSizing';
import { getSidebarWidthPercentClass } from 'dashboard/helper/sidebarWidthPercentClasses';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
  containerWidth: {
    type: Number,
    default: 0,
  },
  containerRight: {
    type: Number,
    default: 0,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { isOrderSidebarOpen, isContactSidebarOpen, closeContactSidebar } =
  useConversationSidebars();
const { width: windowWidth } = useWindowSize();

const SIDEBAR_WIDTH_SETTING_KEY = 'conversation_contact_sidebar_width';
const DEFAULT_SIDEBAR_WIDTH_PERCENT = 30;

const isResizing = ref(false);
const isResizePending = ref(false);
const resizingWidthPercent = ref(null);
let animationFrameId = null;
let resizeStartX = null;

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const sidebarWidth = computed(() => {
  if (isResizing.value && resizingWidthPercent.value !== null) {
    return resizingWidthPercent.value;
  }

  const savedWidth = Number(uiSettings.value[SIDEBAR_WIDTH_SETTING_KEY]);
  return Number.isFinite(savedWidth) && savedWidth >= 0
    ? Math.round(savedWidth)
    : DEFAULT_SIDEBAR_WIDTH_PERCENT;
});

const sidebarWidthClass = computed(() => {
  return getSidebarWidthPercentClass(sidebarWidth.value);
});

const closeContactPanel = () => {
  if (!isSmallScreen.value) return;
  if (!isContactSidebarOpen.value) return;
  closeContactSidebar();
  updateUISettings({ is_copilot_panel_open: false });
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const calculateWidthFromMouseEvent = event => {
  const availableWidth =
    props.containerWidth || window.innerWidth || windowWidth.value;
  if (!availableWidth) return DEFAULT_SIDEBAR_WIDTH_PERCENT;

  const pointerX = event.clientX;
  const containerRight =
    props.containerRight || window.innerWidth || windowWidth.value;
  const distanceFromRight = containerRight - pointerX;
  const rawPercent = (distanceFromRight / availableWidth) * 100;
  const roundedPercent = Math.round(clamp(rawPercent, 0, 90));

  const orderWidthPercent = Number(
    uiSettings.value.conversation_order_sidebar_width || 30
  );
  return clampSidebarWidthPercent({
    nextWidthPercent: roundedPercent,
    otherSidebarOpen: isOrderSidebarOpen.value,
    otherSidebarWidthPercent: orderWidthPercent,
  });
};

const RESIZE_DRAG_THRESHOLD_PX = 6;

const onMouseMove = event => {
  event.preventDefault();
  if (!isResizing.value && !isResizePending.value) return;

  if (isResizePending.value && resizeStartX !== null) {
    const deltaX = Math.abs(event.clientX - resizeStartX);
    if (deltaX < RESIZE_DRAG_THRESHOLD_PX) return;

    isResizePending.value = false;
    isResizing.value = true;
  }

  if (animationFrameId) return;

  animationFrameId = window.requestAnimationFrame(() => {
    resizingWidthPercent.value = calculateWidthFromMouseEvent(event);
    animationFrameId = null;
  });
};

const stopResizing = () => {
  if (!isResizing.value && !isResizePending.value) return;

  const shouldPersist = isResizing.value;

  isResizing.value = false;
  isResizePending.value = false;
  resizeStartX = null;
  const nextWidth = resizingWidthPercent.value ?? DEFAULT_SIDEBAR_WIDTH_PERCENT;
  resizingWidthPercent.value = null;

  if (shouldPersist) {
    updateUISettings({
      [SIDEBAR_WIDTH_SETTING_KEY]: nextWidth,
    });
  }

  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('mouseup', stopResizing);
};

const startResizing = event => {
  if (isSmallScreen.value) return;

  event.preventDefault();
  event.stopPropagation();
  isResizePending.value = true;
  resizeStartX = event.clientX;
  resizingWidthPercent.value = null;

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', stopResizing);
};

onBeforeUnmount(() => {
  if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('mouseup', stopResizing);
});
</script>

<template>
  <div
    v-on-click-outside="() => (!isResizing ? closeContactPanel() : null)"
    class="bg-n-background h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full  transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:relative  md:max-w-none md:shrink-0 ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none" 
    :class="[
      {
        'md:flex': isContactSidebarOpen,
        'md:hidden': !isContactSidebarOpen,
      },
      sidebarWidthClass,
    ]"
  >
    <div class="flex flex-1 flex-col overflow-auto">
      <ContactPanel
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
        :on-resize-start="startResizing"
      />
    </div>
  </div>
</template>
